import flowRight from 'lodash/flowRight';
import { connect } from 'react-redux';

import { trackEventFromState } from '@eb/site-analytics';
import { withMainControls } from '@eb/eds-structure';

import AccessPage from './access/Page';
import ChangePlanPage from './changePlan/Page';
import ConfirmPlanChangePage from './confirmPlanChange/Page';
import DowngradeConflictPage from './downgradeConflicts/Page';
import {
    AFTER_DOWNGRADE as AFTER_DOWNGRADE_TITLE,
    CURRENT_PACKAGE as BEFORE_DOWNGRADE_TITLE,
    defaultDowngradePackage,
    DOWNGRADE_CTA,
    getFeatures,
    getHeading,
    KEEP_PROFESSIONAL_CTA,
    PACKAGE_DOWNGRADE_BREADCRUMB,
    TABLE_TITLE,
    TABLE_TITLE_SHORT,
} from '../constants/downgrade';
import { PACKAGE_ONE_NAME, PACKAGE_TWO_NAME } from '../constants/main';
import {
    AFTER_DOWNGRADE as AFTER_UPDATE_SUBTITLE,
    AFTER_UPDATE,
    CURRENT_PACKAGE as BEFORE_UPDATE_TITLE,
    KEEP_CURRENT,
    PACKAGE_UPDATE_BREADCRUMB,
    PACKAGE_UPDATE_FEATURES,
    PACKAGE_UPDATE_HEADING,
    UPDATE_CTA,
} from '../constants/update';
import DowngradePage from './downgrade/Page';
import OverviewPage from './overview/Page';
import PackageComparisonPage from './packageComparison/Page';
import PackageOnePage from './packageOne/Page';
import PackageTwoPage from './packageTwo/Page';
import PackageThreePage from './packageThree/Page';
import PricingPage from './pricing/Page';
import SignUpPage from './signUp/Page';
import SalesPage from './sales/Page';
import UpdatePage from './update/Page';
import {
    clearSelectedPackage,
    goToPackageComparison,
    goToOverviewPage,
    goToPackageOne,
    goToPackageThree,
    goToPackageTwo,
    goToPricing,
} from '../actions/routing';
import {
    redirectToChangePlan,
    redirectToContactSales,
    redirectToCreate,
    redirectToEssentials,
    redirectToPremium,
    redirectToProfessional,
    redirectToSignIn,
} from '../actions/redirect';
import {
    setSignupPackageOne,
    setSignupPackageThree,
    setSignupPackageTwo,
    setSignupPackageTwoAddon,
} from '../actions/onboarding';
import {
    contactUsRedirect,
    goToDowngrade,
    goToUpdateVersionTwo,
    onChangePackageAfterConflicts,
    onKeepPackage,
    onKeepPackageAfterConflicts,
    setPackageOne,
    setPackageOneVersionTwo,
    setPackageTwo,
} from '../actions/changePlan';
import { planRedirect } from '../actions/addons';
import { checkConflicts, fetchConflicts } from '../actions/downgradeConflicts';
import { isEssentialsNewVersion } from './utils';

// questions about how TrackEventFromState gets here
const _mapStateToProps = ({
    assortments: {
        assortmentsAvailability,
        assortmentsPricing,
        assortmentsTax,
        hasAssortmentsTax,
        hasFixedFee,
        feeBreakdown,
        packageTitles,
        selectedPackage,
    },
    env: { currencyFormat, defaultCurrency, domain, locale, tld },
    featureFlags: {
        crazyEggFeatureFlag,
        cappedFeeExperimentFeatureFlag,
        enablePricingCalculator,
        isPackageChangeDowngradeEnabled,
        useRefactoredPricingTableComparison,
    },
    user: {
        activeScopeUserId,
        isAuthenticated,
        userId,
        userPlan,
        userType,
        activeOrganization,
    },
    eid,
    gaSettings,
    addons: { subscriptionEndDate },
    showLowAtvUi,
    currencySymbol,
    localeDefaultPlan,
}) => ({
    activeScopeUserId,
    assortmentsAvailability,
    assortmentsPricing,
    assortmentsTax,
    hasAssortmentsTax,
    feeBreakdown,
    hasFixedFee,
    packageTitles,
    selectedPackage,
    crazyEggFeatureFlag,
    cappedFeeExperimentFeatureFlag,
    enablePricingCalculator,
    domain,
    locale,
    tld,
    isAuthenticated,
    trackEvent: (action) =>
        trackEventFromState({ gaSettings, eventId: eid }, action),
    userId,
    userType,
    userPlan,
    activeOrganization,
    subscriptionEndDate,
    showLowAtvUi,
    currencySymbol,
    localeDefaultPlan,
    defaultCurrency,
    currencyFormat,
    isPackageChangeDowngradeEnabled,
    useRefactoredPricingTableComparison,
});

const _mapDispatchToProps = {
    clearSelectedPackage,
    contactUsRedirect,
    goToDowngrade,
    goToPricing,
    goToPackageComparison,
    goToOverviewPage,
    goToPackageOne,
    goToPackageTwo,
    goToPackageThree,
    goToUpdateVersionTwo,
    planRedirect,
    redirectToContactSales,
    redirectToSignIn,
    redirectToCreate,
    redirectToChangePlan,
    redirectToEssentials,
    redirectToProfessional,
    redirectToPremium,
    setSignupPackageOne,
    setSignupPackageTwo,
    setSignupPackageTwoAddon,
    setSignupPackageThree,
    setPackageOne,
    setPackageOneVersionTwo,
    setPackageTwo,
};

const _updateMapStateToProps = ({
    assortments: { assortmentsAvailability },
    eid,
    env: { locale },
    featureFlags: { isPackageChangeDowngradeEnabled },
    gaSettings,
    user: { userId, userPlan },
}) => ({
    assortmentsAvailability,
    breadcrumb: PACKAGE_UPDATE_BREADCRUMB,
    changePackageCTA: UPDATE_CTA,
    currentPackageTitle: BEFORE_UPDATE_TITLE,
    features: PACKAGE_UPDATE_FEATURES,
    firstHeading: PACKAGE_UPDATE_HEADING,
    isPackageChangeDowngradeEnabled,
    keepPackageCTA: KEEP_CURRENT,
    locale,
    newPackageSubtitle: AFTER_UPDATE_SUBTITLE,
    newPackageTitle: AFTER_UPDATE,
    trackEvent: (action) =>
        trackEventFromState({ gaSettings, eventId: eid }, action),
    userId,
    userPlan,
});

const _updateMapDispatchToProps = {
    onChangePackage: setPackageOneVersionTwo,
    onKeepPackage,
};

const _downgradeMapStateToProps = ({
    assortments: { assortmentsAvailability },
    featureFlags: { isPackageChangeDowngradeEnabled },
    env: { locale },
    eid,
    gaSettings,
    user: { userId, userPlan },
}) => {
    const downgrade =
        assortmentsAvailability === null
            ? defaultDowngradePackage
            : assortmentsAvailability.downgrade;

    // Experiment in Australia is using a new version of essentials
    const useNewVersion = isEssentialsNewVersion(locale, downgrade);
    const firstHeading = getHeading(
        useNewVersion,
        isPackageChangeDowngradeEnabled,
    );

    return {
        assortmentsAvailability,
        breadcrumb: PACKAGE_DOWNGRADE_BREADCRUMB,
        changePackageCTA: DOWNGRADE_CTA,
        currentPackageSubtitle: BEFORE_DOWNGRADE_TITLE,
        currentPackageTitle: PACKAGE_TWO_NAME,
        features: getFeatures(useNewVersion),
        firstHeading,
        isPackageChangeDowngradeEnabled,
        keepPackageCTA: KEEP_PROFESSIONAL_CTA,
        locale,
        newPackageSubtitle: AFTER_DOWNGRADE_TITLE,
        newPackageTitle: PACKAGE_ONE_NAME,
        tableTitle: TABLE_TITLE,
        tableTitleShort: TABLE_TITLE_SHORT,
        trackEvent: (action) =>
            trackEventFromState({ gaSettings, eventId: eid }, action),
        userId,
        userPlan,
    };
};

const _downgradeMapDispatchToProps = {
    onChangePackage: checkConflicts,
    onKeepPackage,
};

const connectPage = (ComponentClass) =>
    connect(_mapStateToProps, _mapDispatchToProps)(ComponentClass);

export const ConnectedOverviewPage = connectPage(OverviewPage);
export const ConnectedPricingPage = flowRight(
    connect(_mapStateToProps, _mapDispatchToProps),
    withMainControls,
)(PricingPage);
export const ConnectedPackageComparisonPage = connectPage(
    PackageComparisonPage,
);
export const ConnectedPackageOnePage = connectPage(PackageOnePage);
export const ConnectedPackageTwoPage = connectPage(PackageTwoPage);
export const ConnectedPackageThreePage = connectPage(PackageThreePage);
export const ConnectedSignUpPage = connectPage(SignUpPage);
export const ConnectedChangePlanPage = flowRight(
    connect(_mapStateToProps, _mapDispatchToProps),
    withMainControls,
)(ChangePlanPage);
export const ConnectedDowngradePage = connect(_mapStateToProps, {
    ..._mapDispatchToProps,
    onKeepPackage,
})(DowngradePage);
export const ConfirmPlanConnectedDowngradePage = connect(
    _downgradeMapStateToProps,
    _downgradeMapDispatchToProps,
)(ConfirmPlanChangePage);
export const ConnectedUpdatePage = connect(_mapStateToProps, {
    ..._mapDispatchToProps,
    onKeepPackage,
})(UpdatePage);
export const ConfirmPlanConnectedUpdatePage = connect(
    _updateMapStateToProps,
    _updateMapDispatchToProps,
)(ConfirmPlanChangePage);
export const ConnectedSalesPage = connectPage(SalesPage);
export const ConnectedAccessPage = connectPage(AccessPage);
export const ConnectedPackageDowngradeConflictsPage = flowRight(
    connect(
        ({ conflicts, fetchingConflicts, ...state }) => ({
            ..._downgradeMapStateToProps(state),
            conflicts,
            isConflictsLoading: fetchingConflicts,
        }),
        {
            onPageChange: fetchConflicts,
            onRedirectToDowngrade: goToDowngrade,
            onChangePackage: onChangePackageAfterConflicts,
            onKeepPackage: onKeepPackageAfterConflicts,
        },
    ),
    withMainControls,
)(DowngradeConflictPage);
